import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { tasksApi, knowledgeBasesApi } from '../utils/api';
import { AxiosError } from 'axios';
import FileUploader from '../components/FileUploader';
import type { KnowledgeBaseListItem } from '../types/knowledgeBase';
import type { Task } from '../types/task';
import { TaskStatus } from '../types/task';

interface WizardStep {
  number: number;
  title: string;
}

const steps: WizardStep[] = [
  { number: 1, title: 'Task Details' },
  { number: 2, title: 'Upload Files' },
  { number: 3, title: 'Knowledge Bases' },
  { number: 4, title: 'Review' }
];

export default function TaskEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [selectedKBIds, setSelectedKBIds] = useState<number[]>([]);
  const [existingFiles, setExistingFiles] = useState<Array<{id: number; file_name: string; file_size: number}>>([]);

  // KB data
  const [availableKBs, setAvailableKBs] = useState<KnowledgeBaseListItem[]>([]);
  const [defaultKBIds, setDefaultKBIds] = useState<number[]>([]);
  const [isLoadingKBs, setIsLoadingKBs] = useState(false);

  // UI state
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load draft task data
  useEffect(() => {
    const loadDraftTask = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const task: Task = await tasksApi.getById(parseInt(id));

        // Ensure it's a draft task
        if (task.status !== TaskStatus.DRAFT) {
          navigate(`/tasks/${id}`);
          return;
        }

        // Pre-fill form with task data
        setTitle(task.title);
        setDescription(task.description);

        // Load associated knowledge bases
        const kbIds = task.knowledge_bases?.map(kb => kb.id) || [];
        setSelectedKBIds(kbIds);

        // Load existing files (only input files, not iteration output files)
        const inputFiles = task.files.filter(f => f.iteration_id === null);
        setExistingFiles(inputFiles.map(f => ({
          id: f.id,
          file_name: f.file_name,
          file_size: f.file_size
        })));

        setError('');
      } catch (err) {
        setError('Failed to load draft task');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadDraftTask();
  }, [id, navigate]);

  // Load KBs when reaching step 3
  useEffect(() => {
    if (currentStep === 3 && availableKBs.length === 0) {
      loadKnowledgeBases();
    }
  }, [currentStep]);

  const loadKnowledgeBases = async () => {
    try {
      setIsLoadingKBs(true);
      const kbs = await knowledgeBasesApi.getAll(false);
      setAvailableKBs(kbs);

      // Load default KBs
      const response = await fetch('/api/knowledge-bases/defaults', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const defaults = data.knowledge_base_ids || [];
        setDefaultKBIds(defaults);
      }
    } catch (err) {
      console.error('Failed to load knowledge bases:', err);
    } finally {
      setIsLoadingKBs(false);
    }
  };

  const toggleKBSelection = (kbId: number) => {
    if (selectedKBIds.includes(kbId)) {
      setSelectedKBIds(selectedKBIds.filter(id => id !== kbId));
    } else {
      setSelectedKBIds([...selectedKBIds, kbId]);
    }
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleUpdate = async (e: FormEvent, isDraft: boolean = false) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Update task (backend endpoint needed)
      await tasksApi.update(parseInt(id!), {
        title,
        description,
        knowledge_base_ids: selectedKBIds,
        is_draft: isDraft
      });

      // Upload new files if any
      if (files.length > 0) {
        await tasksApi.uploadFiles(parseInt(id!), files);
      }

      if (isDraft) {
        // Stay on edit page
        navigate(`/tasks/${id}/edit`);
      } else {
        // Submit the task
        await tasksApi.submitDraft(parseInt(id!));
        navigate(`/tasks/${id}`);
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>;
      setError(axiosError.response?.data?.detail || 'Failed to update task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!id) return;

    try {
      setIsDeleting(true);
      await tasksApi.delete(parseInt(id));
      navigate('/tasks');
    } catch (err) {
      console.error('Failed to delete draft:', err);
      alert('Failed to delete draft task');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteExistingFile = async (fileId: number) => {
    if (!id) return;

    try {
      // Remove from state immediately for UI feedback
      setExistingFiles(existingFiles.filter(f => f.id !== fileId));

      // Delete from server
      await tasksApi.deleteFile(parseInt(id), fileId);
    } catch (err) {
      console.error('Failed to delete file:', err);
      alert('Failed to delete file');
      // Reload task to restore accurate file list
      const task = await tasksApi.getById(parseInt(id));
      const inputFiles = task.files.filter(f => f.iteration_id === null);
      setExistingFiles(inputFiles.map(f => ({
        id: f.id,
        file_name: f.file_name,
        file_size: f.file_size
      })));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const canProceedFromStep1 = title.trim().length > 0 && description.trim().length > 0;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading draft task...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Edit Task - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
        <div className="mb-8">
          <button
            onClick={() => navigate('/tasks')}
            className="text-sm text-gray-600 hover:text-black mb-4 inline-flex items-center"
          >
            ← Back to Tasks
          </button>
          <h1 className="text-3xl font-bold">Edit Draft Task</h1>
          <p className="mt-2 text-gray-600">
            Update your draft and submit when ready
          </p>
        </div>

        <div className="flex gap-8">
          {/* Vertical Progress Steps - Left Sidebar */}
          <div className="w-48 flex-shrink-0">
        <div className="sticky top-8 space-y-4">
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Editing Draft</h3>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Delete Draft
            </button>
          </div>
          {steps.map((step, index) => (
            <div key={step.number} className="relative">
              <div className="flex items-center space-x-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors flex-shrink-0 ${
                  currentStep >= step.number
                    ? 'border-black bg-black text-white'
                    : 'border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step.number ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-sm font-medium">{step.number}</span>
                  )}
                </div>
                <span className={`text-sm font-medium ${
                  currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`ml-5 mt-2 h-8 w-0.5 transition-colors ${
                  currentStep > step.number ? 'bg-black' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 bg-white border border-gray-200 rounded-lg p-8 shadow-subtle">
        <form onSubmit={handleUpdate}>
          {/* Step 1: Task Details */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">Edit Task Details</h2>
                <p className="text-gray-600">Update the title and description of your task</p>
              </div>

              <div>
                <label htmlFor="wizard-title" className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title *
                </label>
                <input
                  id="wizard-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                  placeholder="e.g., Analyze Q3 Sales Data"
                  required
                  maxLength={200}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {title.length}/200 characters
                </p>
              </div>

              <div>
                <label htmlFor="wizard-description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  id="wizard-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                  placeholder="Provide detailed context and instructions for the agent..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Be specific about what you want the agent to focus on
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Upload Files */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">Manage Files</h2>
                <p className="text-gray-600">View existing files and upload new ones</p>
              </div>

              {/* Existing Files */}
              {existingFiles.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Existing Files</h3>
                  <div className="space-y-2">
                    {existingFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md"
                      >
                        <div className="flex items-center space-x-2">
                          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.file_name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.file_size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteExistingFile(file.id)}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload New Files */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Upload New Files (Optional)</h3>
                <FileUploader
                  onFilesChange={setFiles}
                  initialFiles={files}
                  maxFiles={10}
                  maxSize={10 * 1024 * 1024}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> You can remove existing files or add new ones. All changes will be saved when you update the draft.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Knowledge Bases */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">Select Knowledge Bases (Optional)</h2>
                <p className="text-gray-600">Choose knowledge bases to provide additional context for the agent</p>
              </div>

              {isLoadingKBs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                  <p className="ml-3 text-gray-600">Loading knowledge bases...</p>
                </div>
              ) : availableKBs.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center">
                  <p className="text-gray-600 mb-4">No knowledge bases available</p>
                  <a href="/knowledge-bases/new" className="text-blue-600 hover:underline text-sm">
                    Create your first knowledge base
                  </a>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableKBs.map((kb) => {
                    const isDefault = defaultKBIds.includes(kb.id);
                    const isSelected = selectedKBIds.includes(kb.id);

                    return (
                      <div
                        key={kb.id}
                        className={`border rounded-lg p-4 transition-colors cursor-pointer ${
                          isSelected
                            ? 'border-black bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleKBSelection(kb.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleKBSelection(kb.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-5 h-5 rounded border-gray-300"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-medium text-gray-900">{kb.title}</h3>
                              {isDefault && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  Default
                                </span>
                              )}
                            </div>
                            {kb.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{kb.description}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                              {kb.resource_count} resource{kb.resource_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">Review Your Changes</h2>
                <p className="text-gray-600">Make sure everything looks correct before saving or submitting</p>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Task Title</h3>
                  <p className="text-lg font-medium text-gray-900">{title}</p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{description}</p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">New Files</h3>
                  {files.length > 0 ? (
                    <ul className="text-sm text-gray-900 space-y-1">
                      {files.map((file, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <span>{file.name}</span>
                          <span className="text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No new files to upload</p>
                  )}
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Knowledge Bases</h3>
                  {selectedKBIds.length > 0 ? (
                    <ul className="text-sm text-gray-900 space-y-1">
                      {selectedKBIds.map((kbId) => {
                        const kb = availableKBs.find(k => k.id === kbId);
                        const isDefault = defaultKBIds.includes(kbId);
                        return kb ? (
                          <li key={kbId} className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                            </svg>
                            <span>{kb.title}</span>
                            {isDefault && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                Default
                              </span>
                            )}
                          </li>
                        ) : null;
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No knowledge bases selected</p>
                  )}
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={(e) => currentStep === 1 ? navigate('/tasks') : handleBack(e)}
              className="px-6 py-2 btn-secondary rounded-md text-sm font-medium"
              disabled={isSubmitting}
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </button>

            <div className="flex items-center space-x-3">
              {/* Save as Draft button - available on all steps */}
              <button
                type="button"
                onClick={(e) => handleUpdate(e, true)}
                disabled={isSubmitting || (currentStep === 1 && !canProceedFromStep1)}
                className="px-6 py-2 btn-secondary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={(e) => handleNext(e)}
                  disabled={currentStep === 1 && !canProceedFromStep1}
                  className="px-6 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={(e) => handleUpdate(e, false)}
                  disabled={isSubmitting}
                  className="px-8 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Task'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Delete Draft Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Delete Draft Task?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this draft task? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleDeleteDraft}
                disabled={isDeleting}
                className="px-6 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md text-sm disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-6 py-2 btn-secondary rounded-md text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
    </div>
    </>
  );
}
